import { useState, type JSX } from 'react'
import type { CashPositionMonth } from '../api'
import { compareYen, monthLabel, yen } from '../format'
import { CHART_INK, INFLOW_RAMP, SERIES, hatchSwatch } from '../charts/palette'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'
import { NoDataZone } from '../charts/NoDataZone'
import { monthsAfter } from '../timeWindow'
import type { CashMode } from '../cashMode'
import { PipelineToggle } from './PlanToggle'
import { CashPositionFrame, CashPositionMonthEnd, MonthEndSwitch } from './CashPositionMonthEnd'

// The running cash balance — the net-gain/loss bars integrated into a balance
// line. Anchored on the latest MONTH-END bank balance, so the line passes
// through a real number rather than a modelled one; the anchor month is
// marked. This is the "how much cash do we have, and where is it heading"
// read the sheet's net-per-month bars could never give directly.
//
// THE MAIN LINE IS THE BOOKED VIEW: booked AR, closed fundraise, payroll
// (actual, or the plan's where none is reported) and planned costs. One blue
// series, area-filled to the zero baseline so the runway toward zero is the
// shape the eye follows. It is the same basis as the "Cash in and out" bars
// with the pipeline off, which is the default for both.
//
// THE PIPELINE IS A LAYER, NOT A REPLACEMENT. With the switch on, a second,
// thinner line adds weighted pipeline (deals not yet signed), and the gap
// between the two is hatched like the pipeline segments in the bars. Drawing it
// on top rather than swapping lines means the optimistic case can never be
// mistaken for the current-status one: the difference IS the pipeline.
//
// REPORTED VS PROJECTED. Past the last month with reported payroll, the line is
// no longer integrating things that happened, so that stretch is drawn dashed
// over a shaded ground in both states.
//
// THE ANCHOR IS MONTH-END, NOT LIVE. The freee feed writes the current month's
// balance every two hours; anchoring on that mid-month figure made every
// payment that cleared move every future month (migration 20261015100000).
// The live balance is still worth seeing, so the subtitle states it, and says
// it is not what the line is built from.
//
// TWO READINGS. This file is the 'single-anchor' reading, which renders only
// when 'month-end-actuals' (GTM-54, the reading in use: src/cashMode.ts) is
// off: switched off by a viewer, or forced off by the kill switch. There the
// past months are real month-end bank balances (actual) and the dashed stretch
// is the projection from the latest one; see CashPositionMonthEnd.tsx. The
// switch between them rides in this card's controls, and only appears when the
// parent passes the mode props, so without them this chart is exactly as it was.

const W = 720
const H = 274
const PAD = { top: 18, right: 16, bottom: 48, left: 62 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)

type Row = { month: CashPositionMonth; booked: number; withPipeline: number }

type ChartProps = {
  months: CashPositionMonth[]
  showPipeline: boolean
  onShowPipelineChange: (next: boolean) => void
  /** First month (YYYY-MM-01) not backed by reported payroll, or null. */
  projectedFrom: string | null
  /** freee's latest synced balance for the month still in progress, if any. */
  liveBalance: { month: string; balance: string } | null
  /** Set when a source behind this chart is out of date; see freshness.ts. */
  sourceNote?: string | null
  /** Last month (YYYY-MM-01) of the page's time window; see NoDataZone. */
  windowEnd: string
  /** Which reading of the past to draw. Defaults to today's. */
  mode?: CashMode
  onModeChange?: (next: CashMode) => void
  /** Allow the month-end actuals reading. Absent/false: single-anchor only, no switch. */
  previewAvailable?: boolean
  /** Show the switch between the two readings. Defaults to previewAvailable. */
  showModeSwitch?: boolean
}

export function CashPositionChart(props: ChartProps): JSX.Element {
  const {
    mode = 'single-anchor',
    onModeChange,
    previewAvailable = false,
    showModeSwitch = previewAvailable,
    ...rest
  } = props
  const modeToggle = previewAvailable && showModeSwitch ? (
    <MonthEndSwitch mode={mode} onChange={onModeChange ?? (() => {})} />
  ) : null
  if (previewAvailable && mode === 'month-end-actuals') {
    return (
      <CashPositionMonthEnd
        months={rest.months}
        showPipeline={rest.showPipeline}
        onShowPipelineChange={rest.onShowPipelineChange}
        liveBalance={rest.liveBalance}
        sourceNote={rest.sourceNote}
        windowEnd={rest.windowEnd}
        modeToggle={modeToggle}
      />
    )
  }
  return <SingleAnchorChart {...rest} modeToggle={modeToggle} />
}

function SingleAnchorChart({
  months,
  showPipeline,
  onShowPipelineChange,
  projectedFrom,
  liveBalance,
  sourceNote,
  windowEnd,
  modeToggle,
}: Omit<ChartProps, 'mode' | 'onModeChange' | 'previewAvailable'> & {
  modeToggle: JSX.Element | null
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null)

  const rows = months
    .map((month) => ({
      month,
      booked: plotValue(month.cashPositionExPipeline),
      withPipeline: plotValue(month.cashPosition),
    }))
    .filter((row): row is Row => row.booked !== null && row.withPipeline !== null)

  if (rows.length === 0) {
    // Empty because the backend returned no months (no month-end anchor), or
    // because every value failed to parse — say the one that is true.
    const unanchored = months.length === 0
    // Same title line as a drawn chart, so the switches stay where they were.
    return (
      <CashPositionFrame
        sourceNote={sourceNote}
        switches={
          <>
            <PipelineToggle
              on={showPipeline}
              onChange={onShowPipelineChange}
              affected={0}
              total={0}
            />
            {modeToggle}
          </>
        }
      >
        <p className="notice">
          {unanchored
            ? 'No month-end bank balance on record, so the running balance has nothing to anchor on. It appears once a month with a bank balance has closed; it is never drawn off a guessed seed.'
            : 'The cash position figures in range could not be read as numbers, so no line is drawn.'}
        </p>
      </CashPositionFrame>
    )
  }

  const anchorMonth = rows[0]?.month.anchorMonth ?? null
  const anchorFromFreee = rows[0]?.month.anchorSource === 'freee'

  const allIndices = rows.map((_, index) => index)
  const projectedIndex =
    projectedFrom === null ? -1 : rows.findIndex((row) => row.month.month >= projectedFrom)
  const reportedIndices =
    projectedIndex < 0 ? allIndices : allIndices.filter((index) => index < projectedIndex)
  // The projected run reaches back one point so the line stays continuous
  // across the boundary instead of restarting in mid-air.
  const projectedIndices =
    projectedIndex < 0 ? [] : allIndices.filter((index) => index >= Math.max(0, projectedIndex - 1))

  // Months whose balance the pipeline changes. Exact comparison of the server's
  // decimal strings: a balance is cumulative, so this includes months after a
  // pipeline month and, walking back from the anchor, months before one.
  const pipelineCount = rows.filter(
    (row) => compareYen(row.month.cashPosition, row.month.cashPositionExPipeline) !== 0,
  ).length
  const drawPipeline = showPipeline && pipelineCount > 0

  const switches = (
    <>
      <PipelineToggle
        on={showPipeline}
        onChange={onShowPipelineChange}
        affected={pipelineCount}
        total={rows.length}
      />
      {modeToggle}
    </>
  )

  // Domain over BOTH lines in both states, so switching the pipeline never
  // rescales the axis under the booked line.
  const { min, max } = domainFor([
    ...rows.map((row) => row.booked),
    ...rows.map((row) => row.withPipeline),
  ])
  // Months the window covers past the last one with data keep their place on the
  // axis; the line and area stop at the last month rather than extending.
  const lastMonth = rows[rows.length - 1]?.month.month ?? windowEnd
  const gap = monthsAfter(lastMonth, windowEnd)
  const band = PLOT_W / (rows.length + gap.length)
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const zero = y(0)
  const centre = (index: number): number => PAD.left + band * index + band / 2
  const bookedAt = (index: number): number => rows[index]?.booked ?? 0
  const pipelineAt = (index: number): number => rows[index]?.withPipeline ?? 0
  const pointsOf = (indices: number[], at: (index: number) => number): string =>
    indices.map((index) => `${centre(index)},${y(at(index))}`).join(' ')

  const first = 0
  const last = rows.length - 1
  // Area closes down to the zero baseline under the booked line.
  const areaPoints = `${centre(first)},${zero} ${pointsOf(allIndices, bookedAt)} ${centre(last)},${zero}`
  // The pipeline band: up the pipeline line, back down the booked one.
  const bandPoints = `${pointsOf(allIndices, pipelineAt)} ${pointsOf([...allIndices].reverse(), bookedAt)}`
  const boundaryX = projectedIndex < 0 ? null : PAD.left + band * projectedIndex

  const latest = rows[last]
  const active = hover === null ? null : (rows[hover] ?? null)
  const hoveredGap = hover === null ? null : (gap[hover - rows.length] ?? null)
  const isProjected = (index: number): boolean => projectedIndex >= 0 && index >= projectedIndex
  const anchorLabel = anchorFromFreee ? 'freee bank balance' : 'audited bank balance'
  // The anchor can sit outside the queried range. Its legend entry goes with
  // it then: a legend promising a marker that is nowhere on the chart sends the
  // reader hunting for something that is not there.
  const anchorDrawn = anchorMonth !== null && rows.some((row) => row.month.month === anchorMonth)

  const notes = (
    <>
      <p className="chart-sub">
        Running bank balance from booked AR, closed fundraising, payroll and planned costs
        {latest ? <>: {yen(latest.month.cashPositionExPipeline)} by {monthLabel(latest.month.month)}</> : null}
        {drawPipeline && latest ? (
          <>
            , or {yen(latest.month.cashPosition)} if the weighted pipeline (deals not yet
            signed, the hatched band) comes in
          </>
        ) : null}
        .{' '}
        {anchorMonth ? (
          <>
            It starts from the {anchorLabel} at the end of {monthLabel(anchorMonth)}, so it
            only moves when the forecast inputs change or a month closes.
          </>
        ) : null}
        {liveBalance ? (
          <>
            {' '}freee&apos;s live bank balance for {monthLabel(liveBalance.month)} so far is{' '}
            {yen(liveBalance.balance)}; payments clearing mid-month change that figure, not
            this line.
          </>
        ) : null}
        {projectedIndex >= 0 && rows[projectedIndex] ? (
          <>
            {' '}The dashed stretch from {monthLabel(rows[projectedIndex]?.month.month ?? '')} is
            projected: no payroll has been reported for those months, so the balance past that
            point is a forecast rather than a record.
          </>
        ) : null}
      </p>
      <ul className="legend">
        <li>
          <span className="swatch line" style={{ background: SERIES.blue }} /> Cash on hand
        </li>
        {drawPipeline && (
          <li>
            <span className="swatch" style={{ background: hatchSwatch(INFLOW_RAMP.ar) }} />{' '}
            Pipeline (weighted)
          </li>
        )}
        {projectedIndex >= 0 && (
          <li>
            <span className="swatch line dashed" /> Projected
          </li>
        )}
        {anchorDrawn && (
          <li>
            <span className="swatch anchor" /> Month-end anchor
          </li>
        )}
      </ul>
    </>
  )

  return (
    <CashPositionFrame sourceNote={sourceNote} switches={switches} notes={notes}>
      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart"
          role="img"
          aria-label={
            drawPipeline
              ? 'Running cash balance by month from booked money and expected costs, anchored on the latest month-end bank balance, with a second line adding weighted pipeline. The dashed stretch is projected. The monthly net figures behind it are in the cash flow table below.'
              : 'Running cash balance by month from booked money and expected costs, anchored on the latest month-end bank balance; pipeline is left out. The dashed stretch is projected. The monthly net figures behind it are in the cash flow table below.'
          }
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id="cashArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SERIES.blue} stopOpacity={0.32} />
              <stop offset="100%" stopColor={SERIES.blue} stopOpacity={0.02} />
            </linearGradient>
            {/* Same hatch as the pipeline segments in "Cash in and out", so the
                band reads as the same quantity it is. */}
            <pattern
              id="pipelineBand"
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width={6} height={6} fill={INFLOW_RAMP.ar} opacity={0.12} />
              <line x1={0} y1={0} x2={0} y2={6} stroke={INFLOW_RAMP.ar} strokeWidth={1.5} opacity={0.7} />
            </pattern>
          </defs>

          {/* The projected ground: a wash and a boundary rule, so "from here on
              it is a forecast" is legible without reading the legend. Dashed is
              the right meaning for this rule — it marks a threshold, which is
              precisely why gridlines must never borrow it. */}
          {boundaryX !== null && (
            <g>
              <rect
                x={boundaryX}
                y={PAD.top}
                width={Math.max(0, PAD.left + band * rows.length - boundaryX)}
                height={PLOT_H}
                fill="rgba(226, 232, 240, 0.045)"
              />
              <line
                x1={boundaryX}
                x2={boundaryX}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
                stroke={CHART_INK.axis}
                strokeDasharray="3 3"
              />
              <text x={boundaryX + 6} y={PAD.top + 11} className="tick">
                projected
              </text>
            </g>
          )}

          {gap.length > 0 && (
            <NoDataZone
              x={PAD.left + band * rows.length}
              width={band * gap.length}
              top={PAD.top}
              height={PLOT_H}
              lastMonth={lastMonth}
            />
          )}

          {ticksFor(min, max).map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
              />
              <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                {compactYen(tick)}
              </text>
            </g>
          ))}

          <polygon points={areaPoints} fill="url(#cashArea)" stroke="none" />

          {drawPipeline && (
            <>
              <polygon points={bandPoints} fill="url(#pipelineBand)" stroke="none" />
              <polyline
                points={pointsOf(allIndices, pipelineAt)}
                fill="none"
                stroke={INFLOW_RAMP.ar}
                strokeOpacity={0.75}
                strokeWidth={1.25}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </>
          )}

          {reportedIndices.length > 1 && (
            <polyline
              points={pointsOf(reportedIndices, bookedAt)}
              fill="none"
              stroke={SERIES.blue}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
          {projectedIndices.length > 1 && (
            <polyline
              points={pointsOf(projectedIndices, bookedAt)}
              fill="none"
              stroke={SERIES.blue}
              strokeWidth={2}
              strokeDasharray="5 4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {rows.map((row, index) => {
            if (row.month.month === anchorMonth) {
              return (
                <rect
                  key={`anchor-${row.month.month}`}
                  x={centre(index) - 4}
                  y={y(row.booked) - 4}
                  width={8}
                  height={8}
                  transform={`rotate(45 ${centre(index)} ${y(row.booked)})`}
                  fill={SERIES.yellow}
                  stroke="#272C47"
                  strokeWidth={1.5}
                />
              )
            }
            // A projected point is drawn hollow: the same series, marked as not
            // reported, so it never reads as a measured balance.
            const projected = isProjected(index)
            return (
              <circle
                key={`pt-${row.month.month}`}
                cx={centre(index)}
                cy={y(row.booked)}
                r={hover === index ? 5 : 3}
                fill={projected ? '#272C47' : SERIES.blue}
                stroke={projected ? SERIES.blue : '#272C47'}
                strokeWidth={2}
              />
            )
          })}

          {[...rows.map((row) => row.month.month), ...gap].map((month, index) => (
            <text
              key={`x-${month}`}
              x={centre(index)}
              y={LABEL_Y}
              className="tick"
              textAnchor="end"
              transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}
            >
              {monthLabel(month)}
            </text>
          ))}

          {rows.map((row, index) => (
            <rect
              key={`hit-${row.month.month}`}
              x={PAD.left + band * index}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
            />
          ))}
          {gap.map((month, offset) => (
            <rect
              key={`hit-${month}`}
              x={PAD.left + band * (rows.length + offset)}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(rows.length + offset)}
            />
          ))}

          {hover !== null && (
            <line
              x1={centre(hover)}
              x2={centre(hover)}
              y1={PAD.top}
              y2={PAD.top + PLOT_H}
              stroke={CHART_INK.axis}
              strokeWidth={1}
              pointerEvents="none"
            />
          )}
        </svg>

        {hover !== null && hoveredGap !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(hoveredGap)}</strong>
            <span className="tooltip-note">No data for this month</span>
          </div>
        )}

        {hover !== null && active !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(active.month.month)}</strong>
            <span>
              Cash on hand <b>{yen(active.month.cashPositionExPipeline)}</b>
            </span>
            {drawPipeline && (
              <span>
                With pipeline <b>{yen(active.month.cashPosition)}</b>
              </span>
            )}
            <span>
              Net that month{' '}
              <b>
                {yen(
                  drawPipeline
                    ? active.month.netCashDifference
                    : active.month.netCashDifferenceExPipeline,
                )}
              </b>
            </span>
            {active.month.month === anchorMonth && (
              <span className="tooltip-note">month-end {anchorLabel}</span>
            )}
            {isProjected(hover) && <span className="tooltip-note">projected: no payroll reported</span>}
          </div>
        )}
      </div>
    </CashPositionFrame>
  )
}
